import React, { useEffect, useRef, useState } from 'react';
import { meuPerfilPublicoService, type MeuPerfilPublico } from '../services/meuPerfilPublicoService';
import { transparenciaService } from '../services/transparenciaService';
import Button from './ui/Button';
import { FIELD_CLASS } from './ui/fieldStyles';

const BIO_MAX = 2000;
const FOTO_MAX_BYTES = 1024 * 1024;

type Campos = {
  biografia: string;
  contatoEmail: string;
  contatoTelefone: string;
  contatoEndereco: string;
  site: string;
  instagram: string;
  facebook: string;
};

const vazio: Campos = {
  biografia: '', contatoEmail: '', contatoTelefone: '', contatoEndereco: '', site: '', instagram: '', facebook: '',
};

const deDados = (d: MeuPerfilPublico): Campos => ({
  biografia: d.biografia ?? '',
  contatoEmail: d.contatoEmail ?? '',
  contatoTelefone: d.contatoTelefone ?? '',
  contatoEndereco: d.contatoEndereco ?? '',
  site: d.site ?? '',
  instagram: d.instagram ?? '',
  facebook: d.facebook ?? '',
});

const mensagemDeErro = (e: unknown, padrao: string): string =>
  (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? padrao;

/** Dados que o próprio parlamentar escolhe exibir na sua página pública de transparência. */
const MeuPerfilPublicoForm: React.FC = () => {
  const [campos, setCampos] = useState<Campos>(vazio);
  const [dados, setDados] = useState<MeuPerfilPublico | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [mensagem, setMensagem] = useState<{ tipo: 'ok' | 'erro'; texto: string } | null>(null);
  const arquivoRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let ativo = true;
    meuPerfilPublicoService.obter()
      .then(d => { if (ativo) { setDados(d); setCampos(deDados(d)); } })
      .catch(e => { if (ativo) setMensagem({ tipo: 'erro', texto: mensagemDeErro(e, 'Não foi possível carregar seus dados públicos.') }); })
      .finally(() => { if (ativo) setCarregando(false); });
    return () => { ativo = false; };
  }, []);

  useEffect(() => {
    if (mensagem?.tipo !== 'ok') return;
    const timer = setTimeout(() => setMensagem(null), 4000);
    return () => clearTimeout(timer);
  }, [mensagem]);

  const labelClass = 'block text-sm font-medium text-fg-muted';
  const inputClass = `${FIELD_CLASS} mt-1`;

  const alterar = (campo: keyof Campos) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setCampos(prev => ({ ...prev, [campo]: e.target.value }));

  const salvar = async (e: React.FormEvent) => {
    e.preventDefault();
    setSalvando(true);
    setMensagem(null);
    try {
      const salvo = await meuPerfilPublicoService.salvar(campos);
      setDados(salvo);
      setCampos(deDados(salvo));
      setMensagem({ tipo: 'ok', texto: 'Dados publicados. Já aparecem na sua página pública.' });
    } catch (err) {
      setMensagem({ tipo: 'erro', texto: mensagemDeErro(err, 'Não foi possível salvar. Tente novamente.') });
    } finally {
      setSalvando(false);
    }
  };

  const escolherFoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const arquivo = e.target.files?.[0];
    e.target.value = '';
    if (!arquivo) return;
    if (arquivo.size > FOTO_MAX_BYTES) {
      setMensagem({ tipo: 'erro', texto: 'A foto deve ter no máximo 1 MB.' });
      return;
    }
    setSalvando(true);
    setMensagem(null);
    try {
      setDados(await meuPerfilPublicoService.enviarFoto(arquivo));
      setMensagem({ tipo: 'ok', texto: 'Foto publicada.' });
    } catch (err) {
      setMensagem({ tipo: 'erro', texto: mensagemDeErro(err, 'Não foi possível enviar a foto.') });
    } finally {
      setSalvando(false);
    }
  };

  const removerFoto = async () => {
    setSalvando(true);
    setMensagem(null);
    try {
      setDados(await meuPerfilPublicoService.removerFoto());
      setMensagem({ tipo: 'ok', texto: 'Foto removida.' });
    } catch (err) {
      setMensagem({ tipo: 'erro', texto: mensagemDeErro(err, 'Não foi possível remover a foto.') });
    } finally {
      setSalvando(false);
    }
  };

  if (carregando) {
    return <p className="p-5 text-sm text-slate-600 dark:text-slate-300">Carregando...</p>;
  }

  const slug = dados?.slug ?? undefined;
  // Em produção o app roda sob /emendas (ver App.tsx).
  const base = window.location.pathname.startsWith('/emendas') ? '/emendas' : '';
  const fotoUrl = slug && dados?.temFoto ? transparenciaService.fotoParlamentarUrl(slug, dados.fotoVersao) : null;

  return (
    <form onSubmit={salvar} className="space-y-5 p-5">
      <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
        Tudo o que você preencher aqui é <strong>público</strong> e aparece, sem login, na sua página do Portal da Transparência.
        Campos em branco não são exibidos. Não informe dados que você não queira divulgar.
      </p>

      {mensagem && (
        <div role="status" className={`rounded-xl border px-4 py-3 text-sm font-medium ${mensagem.tipo === 'ok'
          ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200'
          : 'border-red-200 bg-red-50 text-red-800 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200'}`}>
          {mensagem.texto}
        </div>
      )}

      <div>
        <span className={labelClass}>Foto</span>
        <div className="mt-2 flex flex-wrap items-center gap-4">
          {fotoUrl
            ? <img src={fotoUrl} alt="Sua foto pública" className="h-24 w-24 rounded-xl border border-slate-200 object-cover dark:border-slate-700" />
            : <div className="flex h-24 w-24 items-center justify-center rounded-xl border border-dashed border-slate-300 text-xs text-slate-500 dark:border-slate-600 dark:text-slate-400">Sem foto</div>}
          <div className="space-y-2">
            <input ref={arquivoRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={escolherFoto} className="hidden" />
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" disabled={salvando} onClick={() => arquivoRef.current?.click()}>
                {dados?.temFoto ? 'Trocar foto' : 'Enviar foto'}
              </Button>
              {dados?.temFoto && (
                <Button variant="secondary" disabled={salvando} onClick={removerFoto}>
                  Remover
                </Button>
              )}
            </div>
            <p className="text-xs text-fg-muted">JPG, PNG ou WebP, até 1 MB. A foto é publicada assim que enviada.</p>
          </div>
        </div>
      </div>

      <div>
        <label htmlFor="pp-bio" className={labelClass}>Mini biografia</label>
        <textarea id="pp-bio" rows={5} maxLength={BIO_MAX} value={campos.biografia} onChange={alterar('biografia')} className={inputClass} />
        <p className="mt-1 text-right text-xs text-fg-muted">{campos.biografia.length}/{BIO_MAX}</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label htmlFor="pp-email" className={labelClass}>E-mail do gabinete</label>
          <input id="pp-email" type="email" value={campos.contatoEmail} onChange={alterar('contatoEmail')} className={inputClass} />
        </div>
        <div>
          <label htmlFor="pp-tel" className={labelClass}>Telefone do gabinete</label>
          <input id="pp-tel" type="text" value={campos.contatoTelefone} onChange={alterar('contatoTelefone')} className={inputClass} />
        </div>
      </div>
      <div>
        <label htmlFor="pp-end" className={labelClass}>Endereço do gabinete</label>
        <input id="pp-end" type="text" value={campos.contatoEndereco} onChange={alterar('contatoEndereco')} className={inputClass} />
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div>
          <label htmlFor="pp-site" className={labelClass}>Site</label>
          <input id="pp-site" type="text" placeholder="www.seusite.com.br" value={campos.site} onChange={alterar('site')} className={inputClass} />
        </div>
        <div>
          <label htmlFor="pp-insta" className={labelClass}>Instagram</label>
          <input id="pp-insta" type="text" placeholder="instagram.com/seuperfil" value={campos.instagram} onChange={alterar('instagram')} className={inputClass} />
        </div>
        <div>
          <label htmlFor="pp-face" className={labelClass}>Facebook</label>
          <input id="pp-face" type="text" placeholder="facebook.com/seuperfil" value={campos.facebook} onChange={alterar('facebook')} className={inputClass} />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 pt-1">
        <Button type="submit" variant="primary" disabled={salvando}>
          {salvando ? 'Salvando...' : 'Salvar e publicar'}
        </Button>
        {slug && (
          <a href={`${base}/transparencia/parlamentares/${encodeURIComponent(slug)}`} target="_blank" rel="noopener noreferrer"
            className="text-sm font-semibold text-blue-700 underline dark:text-blue-300">
            Ver como o público vê
          </a>
        )}
      </div>
    </form>
  );
};

export default MeuPerfilPublicoForm;
